import React from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  LinearProgress,
  Card,
  CardContent,
  CardMedia,
  CardActions,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh"; // Uncommented
import PhotoIcon from "@mui/icons-material/Photo"; // Uncommented
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import ReactDOM from "react-dom";

const IMAGE_MAX_HEIGHT = 200; // Max height for the image in the card

const UrgentNodeCard = ({
  node,
  dayPercentage,
  nodeColor,
  editingNode,
  editingTitle,
  editingDailyPoints,
  editingUnit,
  newNodeId,
  inputRef,
  showImageButtons,
  imageGeneratingForNodes,
  highlightedNodes,
  setEditingNode,
  setEditingTitle,
  setEditingDailyPoints,
  setEditingUnit,
  handleTitleChange,
  handleTitleSubmit,
  handleKeyPress,
  handleFocus,
  regenerateImageForTitle,
  saveCustomImageToGCS,
  updateNodePoints,
  handleDecrementOpen,
  handleDailyPointsChange,
  handleDailyPointsSubmit,
  handleUnitChange,
  handleUnitSubmit,
  addNewChild,
  moveNodeUp,
  moveNodeDown,
  handleDeleteOpen,
  isUrgent,
  onToggleUrgent,
}) => {
  const theme = useTheme();

  const estimateLines = (text, charsPerLine = 35) => {
    // Adjusted for potentially wider card layout
    if (!text) return 1;
    const lines = text.split("\n").length;
    const approx = Math.ceil(text.length / charsPerLine);
    return Math.max(lines, approx, 1);
  };

  return (
    <Card
      sx={{
        mb: 2, // Add some margin between cards
        backgroundColor:
          highlightedNodes[node.id] === "gain"
            ? "rgba(0,255,0,0.15)"
            : highlightedNodes[node.id] === "lose"
            ? "rgba(255,0,0,0.15)"
            : highlightedNodes[node.id] === "focus"
            ? "rgba(0,0,255,0.15)"
            : "inherit", // Apply highlight based on prop
      }}
    >
      {node.image && (
        <Box sx={{ position: "relative" }}>
          <CardMedia
            component="img"
            image={node.image}
            alt={`${node.title} image`}
            sx={{
              maxHeight: IMAGE_MAX_HEIGHT,
              objectFit: "contain", // Changed to contain to see full image
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              const dialogContainer = document.createElement("div");
              document.body.appendChild(dialogContainer);
              const FullScreenImageDialog = () => {
                const [open, setOpen] = React.useState(true);
                const handleClose = () => {
                  setOpen(false);
                  setTimeout(() => {
                    ReactDOM.unmountComponentAtNode(dialogContainer);
                    document.body.removeChild(dialogContainer);
                  }, 100);
                };
                return (
                  <Dialog fullScreen open={open} onClose={handleClose}>
                    <IconButton
                      onClick={handleClose}
                      sx={{
                        position: "absolute",
                        top: 20,
                        right: 20,
                        color: "white",
                        zIndex: 1000,
                        backgroundColor: "rgba(0, 0, 0, 0.5)", // Make button more visible
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.7)",
                        },
                      }}
                    >
                      <CloseIcon fontSize="large" />
                    </IconButton>
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "black",
                      }}
                    >
                      <Box
                        component="img"
                        src={node.image}
                        alt={`${node.title} full screen`}
                        sx={{ maxWidth: "100%", maxHeight: "100%" }}
                      />
                    </Box>
                  </Dialog>
                );
              };
              ReactDOM.render(<FullScreenImageDialog />, dialogContainer);
            }}
            data-testid="card-node-image" // Added data-testid for image
          />
          {/* {showImageButtons && (
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                display: "flex",
                flexDirection: "column",
                gap: 1, // Add some space between buttons
                backgroundColor: "rgba(255, 255, 255, 0.7)", // Make buttons more visible
                borderRadius: 1,
                p: 0.5,
              }}
            >
              {imageGeneratingForNodes.includes(node.id) ? ( // Changed to node.id
                <Tooltip title="Generating new image">
                  <IconButton
                    size="small"
                    disabled
                    sx={{ color: "orange" }}
                    data-testid="card-image-generating-spinner"
                  >
                    <CircularProgress size={20} color="inherit" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Generate a new AI image">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      regenerateImageForTitle();
                    }}
                    sx={{ color: "orange" }}
                    data-testid="card-generate-ai-image-button" // Added data-testid
                  >
                    <AutoFixHighIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Upload or link a custom image">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    saveCustomImageToGCS();
                  }}
                  sx={{ color: "primary.main" }}
                  data-testid="card-upload-custom-image-button" // Added data-testid
                >
                  <PhotoIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )} */}
        </Box>
      )}

      <CardContent
        sx={{ pt: node.image ? 1 : 2 }}
        data-testid="urgent-node-card"
      >
        {" "}
        {/* Added data-testid to CardContent as a proxy for Card root */}
        {/* Adjust padding if image exists */}
        {/* Title */}
        {(editingNode?.id === node.id || newNodeId === node.id) &&
        editingTitle ? (
          <Tooltip title="Edit the activity title">
            <TextField
              fullWidth // Make text field take full width of card
              size="medium"
              value={editingNode?.title || ""}
              onChange={(e) => handleTitleChange(e, node)}
              data-testid="card-node-title-edit-input" // Added data-testid
              onBlur={() => handleTitleSubmit(node)}
              onKeyDown={(e) => handleKeyPress(e, node, "title")}
              inputRef={inputRef}
              onFocus={handleFocus}
              autoFocus
              multiline
              maxRows={10}
              minRows={estimateLines(editingNode?.title || "")}
              sx={{ width: "100%" }}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Click to edit the activity title">
            <Typography
              variant="h6"
              component="div" // Good for semantic HTML
              data-testid="card-node-title" // Added data-testid
              onClick={(e) => {
                e.stopPropagation();
                setEditingNode(node);
                setEditingTitle(true);
              }}
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                mb: 1, // Add margin bottom
                cursor: "pointer",
              }}
            >
              {node.title}
            </Typography>
          </Tooltip>
        )}
        {node.hasOwnProperty("unit") && (
          <Tooltip title="Unit of measurement for this activity">
            <Box sx={{ display: "flex", alignItems: "center", mb: 1.6 }}>
              {(editingNode?.id === node.id || newNodeId === node.id) &&
              editingUnit ? (
                <TextField
                  size="small"
                  value={editingNode?.unit || ""}
                  onChange={(e) => handleUnitChange(e, node)}
                  onBlur={() => handleUnitSubmit(node)}
                  data-testid="card-node-unit-edit-input" // Added data-testid
                  onKeyDown={(e) => handleKeyPress(e, node, "unit")}
                  inputRef={inputRef}
                  onFocus={handleFocus}
                  autoFocus
                  sx={{ width: "100%" }}
                />
              ) : (
                <span
                  data-testid="card-node-unit-display" // Added data-testid
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingNode(node);
                    setEditingUnit(true);
                  }}
                  style={{
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  {node.unit}
                </span>
              )}
            </Box>
          </Tooltip>
        )}
        {node.title !== "Life" && (
          <>
            {/* Progress Bar */}
            {node.hasOwnProperty("dailyPoints") && node.dailyPoints > 0 && (
              <Box sx={{ width: "100%", mb: 1 }}>
                <Tooltip title="Today's progress toward the daily goal">
                  <LinearProgress
                    variant="determinate"
                    color="success"
                    value={dayPercentage}
                    sx={{ height: 8, borderRadius: 1 }} // Make progress bar thicker
                  />
                </Tooltip>
              </Box>
            )}

            {/* Points and Units */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between", // Space out points and units
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip title="Total points earned for this activity">
                  <Typography
                    variant="body1"
                    sx={{ mr: 0.5, fontWeight: "bold" }}
                  >
                    {node.points} pts
                  </Typography>
                </Tooltip>
                <Tooltip title="Relative progress compared to other activities">
                  <Box
                    sx={{
                      width: "15px",
                      height: "15px",
                      borderRadius: "50%",
                      backgroundColor: nodeColor,
                      ml: 1,
                    }}
                  />
                </Tooltip>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip title="Points earned today / planned for today">
                  <Typography
                    variant="body2"
                    sx={{ mr: 1, whiteSpace: "nowrap" }}
                  >
                    Today: {node.today != null ? node.today : 0}
                    {node.hasOwnProperty("dailyPoints") && (
                      <>
                        {" / "}
                        {(editingNode?.id === node.id ||
                          newNodeId === node.id) &&
                        editingDailyPoints ? (
                          <TextField
                            size="small"
                            value={editingNode?.dailyPoints || ""}
                            onChange={(e) => handleDailyPointsChange(e, node)}
                            onBlur={() => handleDailyPointsSubmit(node)}
                            data-testid="card-node-daily-points-edit-input" // Added data-testid
                            onKeyDown={(e) =>
                              handleKeyPress(e, node, "dailyPoints")
                            }
                            inputRef={inputRef}
                            onFocus={handleFocus}
                            autoFocus
                            sx={{ width: "100%" }}
                            type="number"
                          />
                        ) : (
                          <span
                            data-testid="card-node-daily-points-display" // Added data-testid
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingNode(node);
                              setEditingDailyPoints(true);
                            }}
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                          >
                            {node.dailyPoints}
                          </span>
                        )}
                      </>
                    )}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          </>
        )}
      </CardContent>

      {node.title !== "Life" && (
        <CardActions sx={{ justifyContent: "space-around", pt: 0 }}>
          {" "}
          {/* Use space-around for button distribution */}
          {/* Action Buttons */}
          <Tooltip title="Mark accomplished and gain a point">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                updateNodePoints(node, 1);
              }}
              data-testid="card-increment-points-button" // Added data-testid
              sx={{
                color:
                  highlightedNodes[node.id] === "gain"
                    ? "gold"
                    : highlightedNodes[node.id] === "focus"
                    ? "dodgerblue"
                    : "green",
              }}
            >
              <CheckIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Lose a point for this activity">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDecrementOpen(node);
              }}
              sx={{ color: "red" }}
              data-testid="card-decrement-points-button" // Added data-testid
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={isUrgent ? "Unmark as Urgent" : "Mark as Urgent"}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleUrgent(); // Use the passed prop
              }}
              sx={{ color: isUrgent ? theme.palette.warning.main : "grey.500" }} // Use theme color
              data-testid="card-toggle-urgent-button" // Added data-testid
            >
              <ReportProblemIcon />
            </IconButton>
          </Tooltip>
          {/* "Edit this activity" button removed, direct click on fields */}
          {/* "Add a sub-activity" - Assuming not on card for TodayPage, but could be added with a testid */}
          <Tooltip title="Delete this task">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteOpen(node); // Ensure handleDeleteOpen prop is passed correctly
              }}
              sx={{ color: "grey.700" }} // Consistent color
              data-testid="card-delete-button" // Added data-testid
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
          {/* "Move activity up/down" - Assuming not on card for TodayPage, but could be added with testids */}
        </CardActions>
      )}
    </Card>
  );
};

export default UrgentNodeCard;
